import { useEffect, useState } from "react";
import { CalendarCheck, Phone, Check, X, Clock, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import api, { formatINR, formatApiError } from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BOOKING_STATUS_STYLE, fmtStatus } from "@/constants/status";

const Card = ({ b, onAction, onMarkPaid }) => {
  const paid = b.payment_status === "paid";
  return (
    <div data-testid={`owner-booking-${b.id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-sm font-semibold text-primary">{b.booking_ref}</p>
          <p className="font-heading font-semibold text-stone-900">{b.property_name}</p>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BOOKING_STATUS_STYLE[b.status]}`}>{fmtStatus(b.status)}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-stone-600">
        <p>Customer: <span className="font-medium text-stone-800">{b.customer_name}</span></p>
        <p className="flex items-center gap-1"><Phone size={14} /> {b.customer_phone}</p>
        <p>Date: <span className="font-medium text-stone-800">{b.date}</span></p>
        <p>Guests: <span className="font-medium text-stone-800">{b.guests}</span></p>
        <p>Type: <span className="font-medium text-stone-800">{b.booking_type}</span></p>
        <p>Total value: <span className="font-heading font-bold text-stone-900">{formatINR(b.total_amount)}</span></p>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-accent/40 p-3 text-center text-xs">
        <div><p className="text-stone-500">Advance {paid ? "received" : "expected"}</p><p className={`font-heading font-bold ${paid ? "text-green-700" : "text-stone-700"}`}>{formatINR(b.advance_amount)}</p></div>
        <div><p className="text-stone-500">Avinya commission</p><p className="font-heading font-bold text-stone-700">{formatINR(b.commission_amount)}</p></div>
        <div><p className="text-stone-500">Your earnings</p><p className="font-heading font-bold text-primary">{formatINR(b.owner_amount != null ? b.owner_amount : b.owner_payout)}</p></div>
      </div>
      {b.status === "pending" && (
        <div className="mt-4 flex gap-3">
          <Button data-testid={`accept-${b.id}`} onClick={() => onAction(b.id, "accept")} className="flex-1 gap-1 rounded-xl bg-primary hover:bg-green-900"><Check size={16} /> Accept</Button>
          <Button data-testid={`reject-${b.id}`} variant="outline" onClick={() => onAction(b.id, "reject")} className="flex-1 gap-1 rounded-xl text-destructive"><X size={16} /> Reject</Button>
        </div>
      )}
      {b.status === "awaiting_payment" && (
        <div className="mt-4 space-y-2">
          <p className="flex items-center justify-center gap-1.5 rounded-xl bg-orange-50 py-2 text-xs font-medium text-orange-700"><Clock size={14} weight="fill" /> Waiting for customer's advance payment</p>
          <Button data-testid={`mark-advance-paid-${b.id}`} variant="outline" onClick={() => onMarkPaid(b, "advance")} className="w-full gap-1.5 rounded-xl border-primary/40 text-primary hover:bg-accent"><LinkSimple size={16} /> Mark advance paid (link)</Button>
        </div>
      )}
      {b.status === "confirmed" && b.payment_status === "paid" && !b.balance_paid && Number(b.balance_amount) > 0 && (
        <Button data-testid={`mark-balance-paid-${b.id}`} variant="outline" onClick={() => onMarkPaid(b, "balance")} className="mt-4 w-full gap-1.5 rounded-xl border-primary/40 text-primary hover:bg-accent"><LinkSimple size={16} /> Mark balance paid (link)</Button>
      )}
      {b.status === "confirmed" && (
        <Button data-testid={`complete-${b.id}`} variant="outline" onClick={() => onAction(b.id, "complete")} className="mt-3 w-full rounded-xl">Mark as Completed</Button>
      )}
    </div>
  );
};

export default function OwnerBookings() {
  const [items, setItems] = useState(null);
  const [markTarget, setMarkTarget] = useState(null);
  const load = () => api.get("/owner/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const confirmMarkPaid = async () => {
    if (!markTarget) return;
    try {
      await api.post(`/bookings/${markTarget.b.id}/mark-paid-link`, { type: markTarget.type });
      toast.success(markTarget.type === "advance" ? "Advance marked paid — booking confirmed" : "Balance marked paid");
      setMarkTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const action = async (bid, status) => {
    try {
      await api.patch(`/owner/bookings/${bid}/action`, { status });
      toast.success(`Booking ${status === "accept" ? "accepted" : status === "reject" ? "rejected" : "completed"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const groups = {
    pending: (items || []).filter((b) => b.status === "pending"),
    awaiting: (items || []).filter((b) => b.status === "awaiting_payment"),
    confirmed: (items || []).filter((b) => ["confirmed", "completed"].includes(b.status)),
    all: items || [],
  };

  return (
    <DashboardLayout nav={ownerNav} title="Owner">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Booking Requests</h1>
      <Tabs defaultValue="pending" className="mt-4">
        <TabsList className="rounded-full bg-stone-100">
          <TabsTrigger value="pending" data-testid="ob-tab-pending" className="rounded-full">Pending ({groups.pending.length})</TabsTrigger>
          <TabsTrigger value="awaiting" data-testid="ob-tab-awaiting" className="rounded-full">Awaiting Payment ({groups.awaiting.length})</TabsTrigger>
          <TabsTrigger value="confirmed" data-testid="ob-tab-confirmed" className="rounded-full">Confirmed ({groups.confirmed.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="ob-tab-all" className="rounded-full">All</TabsTrigger>
        </TabsList>
        {Object.entries(groups).map(([k, list]) => (
          <TabsContent key={k} value={k} className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {items === null ? (
              [1, 2].map((i) => <SkeletonRow key={i} />)
            ) : list.length === 0 ? (
              <div className="lg:col-span-2"><EmptyState icon={CalendarCheck} title="No bookings" message="No booking requests in this category yet." /></div>
            ) : (
              list.map((b) => <Card key={b.id} b={b} onAction={action} onMarkPaid={(bk, type) => setMarkTarget({ b: bk, type })} />)
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!markTarget} onOpenChange={(v) => !v && setMarkTarget(null)}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader><DialogTitle>Mark {markTarget?.type} paid via Razorpay link?</DialogTitle></DialogHeader>
          {markTarget && (
            <p className="text-sm text-stone-600">Confirm you've received <span className="font-heading font-bold text-primary">{formatINR(markTarget.type === "advance" ? markTarget.b.advance_amount : markTarget.b.balance_amount)}</span> for <span className="font-mono">{markTarget.b.booking_ref}</span> via your Razorpay payment link. {markTarget.type === "advance" ? "This will CONFIRM the booking." : "This clears the balance."} Only do this after the money is actually in your Razorpay account.</p>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setMarkTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-mark-paid" className="rounded-xl bg-primary hover:bg-green-900" onClick={confirmMarkPaid}>Yes, mark paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
